import { Suspense } from "react";
import { Box } from "@mui/material";
import { Outlet, useLocation } from "react-router-dom";
import { useCerberus } from "../../hooks/useCerberus";
import { useAudience } from "../Access/useAudience";
import ErrorBoundary from "../Common/ErrorBoundary";
import ExternalAccountBanner from "../ExternalProviders/ExternalAccountBanner";
import PageSkeleton from "../Widgets/PageSkeleton";
import NoRolesAssigned from "../Access/NoRolesAssigned";

/**
 * The routed content region: everything the shell wraps around the matched page. One Suspense
 * fallback for the whole tree, where each per-audience route file used to carry its own.
 *
 * The ErrorBoundary here is now only a backstop for this component and the banner — every route
 * carries its own `errorElement` (see `withErrorElement` in src/router.tsx), which is what catches
 * a page failure and, unlike this class boundary, clears itself when the user navigates.
 */
const RoutedContent = () => {
  const { userId, roles } = useCerberus();
  const { audience } = useAudience();
  const location = useLocation();

  // A signed-in account with no role. RootLayout already shows it NoRolesAssigned instead of the
  // shell; this covers the shell-less registration branch. (This used to be an endless <Loading />,
  // on the theory that empty roles meant "not loaded yet" — but roles arrive in the same /identity
  // response as the user id, so for a genuinely roleless account it never finished.)
  if (userId && !roles?.length) {
    return <NoRolesAssigned />;
  }

  // The registration page is its own flow — an as-yet-unregistered provider is also unapproved, and
  // the banner would wrongly tell them their account is "pending approval" while they are still
  // filling in the form.
  const showAccountBanner =
    audience === "external" && location.pathname !== "/register";

  return (
    <ErrorBoundary>
      {/* Owns the height chain for the content region.
          A page that asks for `height: 100%` (Messages, the patient conversation) needs a parent
          with a DEFINITE height. A min-height:100% box does not provide one — a percentage height
          against an auto-height parent resolves to auto, so those pages collapsed to their content
          height. A flex column of height 100% whose page slot is `flex: 1` does provide one, and
          it also subtracts the banner's height instead of letting the page overshoot by it. */}
      <Box
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        {showAccountBanner && (
          <Box sx={{ flexShrink: 0 }}>
            <ExternalAccountBanner />
          </Box>
        )}
        {/* flex:1 + minHeight:0 gives the page exactly the space left over — full height when the
            page is short (so the region never collapses between navigations, which is what the
            skeleton work was for), and content that simply overflows into the parent's scroll when
            the page is tall. flow-root contains a page's own top margin, which would otherwise
            collapse out and overflow the scroll container by its size. */}
        <Box sx={{ flex: 1, minHeight: 0, display: "flow-root" }}>
          <Suspense fallback={<PageSkeleton />}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
    </ErrorBoundary>
  );
};

export default RoutedContent;
